import type { MessageAnchorRect } from "@/hooks/use-chat-message-interactions"

export const REACTION_BAR_HEIGHT = 52
export const REACTION_BTN_SIZE = 36
export const REACTION_BAR_GAP = 2 // gap-0.5
export const REACTION_BAR_PAD_X = 6 // px-1.5
export const REACTION_BAR_BORDER = 1 // border

/**
 * Natural content width of the reaction bar for a given button count.
 * Derived (not hardcoded) so the bar hugs its content — a fixed width wider
 * than the content gets split as slack on both sides by `justify-center`,
 * which reads as oversized, uneven horizontal padding.
 */
export function reactionBarWidth(buttonCount: number): number {
  return (
    buttonCount * REACTION_BTN_SIZE +
    Math.max(0, buttonCount - 1) * REACTION_BAR_GAP +
    REACTION_BAR_PAD_X * 2 +
    REACTION_BAR_BORDER * 2
  )
}
export const STACK_GAP = 12
export const MENU_ROW_HEIGHT = 44
export const MENU_PANEL_PAD = 16
export const VIEWPORT_EDGE_PAD = 8

export type OverlayStackLayout = {
  bubbleTop: number
  bubbleLeft: number
  reactionTop: number
  menuAnchor: { x: number; y: number; w: number; h: number }
  bubbleOffsetY: number
}

export function estimateMenuHeight(menuItemCount: number): number {
  return menuItemCount * MENU_ROW_HEIGHT + MENU_PANEL_PAD
}

export function computeOverlayStack(
  anchor: MessageAnchorRect,
  menuItemCount: number,
  screenH: number,
  insets: { top?: number; bottom?: number } = {},
): OverlayStackLayout {
  const minTop = (insets.top ?? 0) + VIEWPORT_EDGE_PAD
  const maxBottom = screenH - (insets.bottom ?? 0) - VIEWPORT_EDGE_PAD
  const menuHeight = estimateMenuHeight(menuItemCount)

  let bubbleTop = anchor.y
  let stackBottom = bubbleTop + anchor.height + STACK_GAP + menuHeight

  if (stackBottom > maxBottom) {
    bubbleTop -= stackBottom - maxBottom
  }

  let reactionTop = bubbleTop - REACTION_BAR_HEIGHT - STACK_GAP
  if (reactionTop < minTop) {
    bubbleTop += minTop - reactionTop
    reactionTop = minTop
  }

  return {
    bubbleTop,
    bubbleLeft: anchor.x,
    reactionTop,
    menuAnchor: {
      x: anchor.x,
      y: bubbleTop + anchor.height + STACK_GAP,
      w: anchor.width,
      h: 0,
    },
    bubbleOffsetY: bubbleTop - anchor.y,
  }
}
